/**
 * Lead detail.
 *
 * Conversion and the follow-up scheduler live here; everything else is the
 * shared RecordDetail body.
 */

import { useFetcher, useLoaderData } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { Lead } from '@erp/contracts'
import type { Assignment, Note, Task, TimelineResult } from '@erp/contracts'
import { api, ApiError } from '../api'
import { handleSubjectIntent, type ActionResult } from '../detail-actions'
import { Detail, RecordDetail } from '../components/record-detail'
import { AssigneePicker } from '../components/assignee-picker'
import { Badge, Button, Field, PageHeader, Select, TextInput } from '../ui'
import { useLeadStatuses, useLostReasons, useStatus , useUser } from '../status-context'
import { displayName, toLocalInput } from '../format'

interface Data {
  lead: Lead
  timeline: TimelineResult
  notes: Note[]
  tasks: Task[]
  assignments: Assignment[]
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Data> {
  const id = params.leadId
  if (!id) throw new Response('Missing lead id', { status: 400 })

  const [lead, timeline, notes, tasks, assignments] = await Promise.all([
    api.leads.get(id),
    api.leads.timeline(id, { limit: 50 }),
    api.notes.deletedFor('lead', id),
    api.tasks.forSubject('lead', id),
    api.assignments.forSubject('lead', id),
  ])
  return { lead, timeline, notes: notes.rows, tasks: tasks.rows, assignments }
}

/**
 * One action per screen, keyed by `intent`. Returning validation issues rather
 * than throwing lets forms render them inline; react-router revalidates the
 * loader on success, which is what refreshes the timeline.
 */
export async function action({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult & { dealId?: string }> {
  const id = params.leadId
  if (!id) throw new Response('Missing lead id', { status: 400 })

  const form = await request.formData()
  const intent = String(form.get('intent'))

  const shared = await handleSubjectIntent(intent, form)
  if (shared) return shared

  try {
    if (intent === 'status') {
      const reason = form.get('lostReasonId')
      await api.leads.update(id, {
        statusId: String(form.get('statusId')),
        lostReasonId: typeof reason === 'string' && reason.length > 0 ? reason : null,
      })
      return { ok: true }
    }

    if (intent === 'follow-up') {
      const at = String(form.get('at'))
      await api.leads.scheduleFollowUp(id, new Date(at).toISOString())
      return { ok: true }
    }

    if (intent === 'convert') {
      const deal = await api.leads.convert(id, {
        organizationId: null,
        newOrganizationName: form.get('organizationName')?.toString() || null,
        contactId: null,
        createContactFromLead: true,
        deal: {},
      })
      return { ok: true, dealId: deal.id }
    }

    return { ok: false, message: 'Unknown action' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, issues: error.issues }
    throw error
  }
}

export default function LeadDetail() {
  const { lead, timeline, notes, tasks, assignments } = useLoaderData() as Data
  const status = useStatus(lead.statusId)
  const owner = useUser(lead.ownerId)
  const fetcher = useFetcher<typeof action>()

  const converted = lead.convertedAt !== null
  const busy = fetcher.state !== 'idle'

  return (
    <>
      <PageHeader
        title={displayName(lead)}
        actions={
          <>
            <Badge color={status?.color ?? 'gray'}>{status?.name ?? '—'}</Badge>
            {converted ? null : (
              <a className="ax-btn" href={`/crm/leads/${lead.id}/edit`}>
                Edit
              </a>
            )}
            {converted ? (
              <a className="ax-btn" href={`/crm/deals/${lead.convertedDealId}`}>
                View deal
              </a>
            ) : (
              <fetcher.Form method="post">
                <input type="hidden" name="intent" value="convert" />
                <input type="hidden" name="organizationName" value={lead.organizationName ?? ''} />
                <Button variant="solid" type="submit" disabled={busy}>
                  {busy ? 'Converting…' : 'Convert to Deal'}
                </Button>
              </fetcher.Form>
            )}
          </>
        }
      />

      {fetcher.data && !fetcher.data.ok ? (
        <div className="ax-alert ax-alert-danger">
          {fetcher.data.message ?? 'Something went wrong'}
        </div>
      ) : null}

      <RecordDetail
        subject={{ type: 'lead', id: lead.id }}
        timeline={timeline}
        notes={notes}
        tasks={tasks}
        side={
          <>
            <AssigneePicker subject={{ type: 'lead', id: lead.id }} assignments={assignments} />
            <StatusPicker lead={lead} disabled={converted} />
            <FollowUp lead={lead} disabled={converted} />
            <Detail label="Owner" value={owner?.name ?? null} />
            <Detail label="Email" value={lead.email} />
            <Detail label="Phone" value={lead.phone} />
            <Detail label="Organization" value={lead.organizationName} />
            <Detail label="Website" value={lead.website} />
            <Detail label="Segment" value={lead.segment} />
            <Detail label="Follow-ups" value={lead.followUpCount} />
          </>
        }
      />
    </>
  )
}

function StatusPicker({ lead, disabled }: { lead: Lead; disabled: boolean }) {
  const statuses = useLeadStatuses()
  const lostReasons = useLostReasons()
  const fetcher = useFetcher()

  const pending = fetcher.formData?.get('statusId')
  const selectedId = typeof pending === 'string' ? pending : lead.statusId
  const selected = statuses.find((s) => s.id === selectedId)

  // A terminal status needs a reason in the same request — the database rejects
  // it otherwise — so the form waits rather than submitting on change.
  const needsReason = selected?.isTerminal ?? false

  return (
    <fetcher.Form method="post">
      <input type="hidden" name="intent" value="status" />
      <Field label="Status">
        <Select
          name="statusId"
          value={selectedId}
          disabled={disabled || fetcher.state !== 'idle'}
          options={statuses.map((s) => ({ value: s.id, label: s.name }))}
          onChange={(next) => {
            const target = statuses.find((s) => s.id === next)
            if (!target?.isTerminal) {
              fetcher.submit({ intent: 'status', statusId: next }, { method: 'post' })
            }
          }}
        />
      </Field>

      {needsReason ? (
        <>
          <Field label="Reason">
            <Select
              name="lostReasonId"
              value={lead.lostReasonId ?? ''}
              options={[
                { value: '', label: 'Select a reason…' },
                ...lostReasons.map((reason) => ({ value: reason.id, label: reason.name })),
              ]}
            />
          </Field>
          <Button variant="danger" type="submit">
            Close lead
          </Button>
        </>
      ) : null}
    </fetcher.Form>
  )
}

/**
 * Replaces the six Follow Up statuses the old pipeline used. The lead keeps its
 * real stage; only the date moves.
 */
function FollowUp({ lead, disabled }: { lead: Lead; disabled: boolean }) {
  const fetcher = useFetcher()
  const overdue = lead.nextFollowUpAt !== null && new Date(lead.nextFollowUpAt) <= new Date()

  return (
    <fetcher.Form method="post">
      <input type="hidden" name="intent" value="follow-up" />
      <Field label={overdue ? 'Next follow-up (overdue)' : 'Next follow-up'}>
        <TextInput
          name="at"
          type="datetime-local"
          defaultValue={lead.nextFollowUpAt ? toLocalInput(lead.nextFollowUpAt) : ''}
        />
      </Field>
      <Button type="submit" disabled={disabled || fetcher.state !== 'idle'}>
        Schedule
      </Button>
    </fetcher.Form>
  )
}


